import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PriorityBadge, StatusBadge, CategoryBadge } from "../components/Badges";
import { PRIORITIES, PRIORITY_LABEL, STATUSES, STATUS_LABEL, CATEGORIES, CATEGORY_LABEL, relTime, slaCountdown } from "../lib/labels";
import { Switch } from "../components/ui/switch";
import { MagnifyingGlass, Plus, Clock, FunnelSimple, Trash, ShieldWarning, ShieldCheck, X } from "@phosphor-icons/react";
import { openTicketFromEvent } from "../lib/openTicket";

export default function Tickets() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const clientIdFilter = searchParams.get("client_id");
  const urlStatus = searchParams.get("status"); // deep-link from Kanban "Voir les N fermés"
  const isStaff = user?.role === "admin" || user?.role === "technicien";
  const isAdmin = user?.role === "admin";
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [clientName, setClientName] = useState("");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(urlStatus && ["nouveau","en_cours","en_attente","resolu","ferme"].includes(urlStatus) ? urlStatus : "__all");
  const [priority, setPriority] = useState("__all");
  const [category, setCategory] = useState("__all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortMode, setSortMode] = useState("newest");
  const [view, setView] = useState("all"); // "all" | "mine" | "untaken" | "spam" | "ai"
  const [hideClosed, setHideClosed] = useState(() => {
    // Deep-link to a closed filter (from Kanban) must NOT be hidden by the toggle.
    if (urlStatus === "ferme" || urlStatus === "resolu") return false;
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("mt.tickets.hideClosed") : null;
    return saved === null ? true : saved === "true"; // default: hide closed
  });
  const [hideAi, setHideAi] = useState(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("mt.tickets.hideAi") : null;
    return saved === "true"; // default: show AI tickets
  });
  useEffect(() => {
    try { window.localStorage.setItem("mt.tickets.hideClosed", String(hideClosed)); } catch { /* ignore */ }
  }, [hideClosed]);
  useEffect(() => {
    try { window.localStorage.setItem("mt.tickets.hideAi", String(hideAi)); } catch { /* ignore */ }
  }, [hideAi]);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  const seenBreachedRef = useRef(new Set());
  const loadRef = useRef(() => {});
  useEffect(() => {
    // Fire the LATEST load() (which reads current q/status/priority/etc.) instead of a
    // stale closure captured when the effect first ran. This is what preserved the search
    // across autorefreshes.
    const id = setInterval(() => { loadRef.current?.(); }, 60000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    setLoading(true);
    const params = { page, limit: 25 };
    if (q) params.q = q;
    if (status !== "__all") params.status = status;
    if (priority !== "__all") params.priority = priority;
    if (category !== "__all") params.category = category;
    if (view === "mine" && user?.id) params.assignee_id = user.id;
    if (view === "untaken") params.taken = "no";
    if (view === "unread") params.unread = "only";
    if (view === "spam") params.spam = "only";
    if (view === "ai") params.ai = "only";
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (sortMode) params.sort = sortMode;
    if (hideClosed && view !== "spam" && view !== "untaken" && view !== "unread") params.hide_closed = "true";
    if (hideAi && view !== "ai") params.hide_ai = "true";
    const r = await api.get("/tickets", { params });
    let payload = r.data;
    // support both legacy array and new paginated shape
    let list = Array.isArray(payload) ? payload : (payload.items || []);
    const total = Array.isArray(payload) ? list.length : (payload.total || list.length);
    const pages = Array.isArray(payload) ? 1 : (payload.pages || 1);
    if (clientIdFilter) {
      list = list.filter((t) => t.client_id === clientIdFilter);
      if (list[0]) setClientName(list[0].client_name);
    }
    // Detect newly-breached SLAs (only for staff, not on first load)
    if (isStaff) {
      const now = Date.now();
      const newBreaches = list.filter((t) => {
        if (!t.sla_resolution_deadline) return false;
        if (t.status === "resolu" || t.status === "ferme") return false;
        const dl = new Date(t.sla_resolution_deadline).getTime();
        if (dl >= now) return false;
        if (seenBreachedRef.current.has(t.id)) return false;
        seenBreachedRef.current.add(t.id);
        return true;
      });
      // Skip the very first pass (populate ref silently)
      if (seenBreachedRef.current.__inited && newBreaches.length > 0) {
        newBreaches.slice(0, 3).forEach((t) => {
          toast.error(`SLA dépassé — #${t.number} ${t.subject}`, {
            action: { label: "Voir", onClick: () => nav(`/tickets/${t.id}`) },
            duration: 8000,
          });
        });
      }
      seenBreachedRef.current.__inited = true;
    }
    setTickets(list); setTotal(total); setPages(pages);
    setSelected(new Set()); // clear selection whenever the list is (re)loaded
    setLoading(false);
  };
  // Keep the ref pointing at the latest load() so setInterval always calls the
  // fresh closure — that's what preserves the search box across the 60s autorefresh.
  loadRef.current = load;

  const toggleOne = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const allChecked = tickets.length > 0 && tickets.every((t) => selected.has(t.id));
  const someChecked = tickets.some((t) => selected.has(t.id)) && !allChecked;
  const toggleAll = (checked) => {
    setSelected(checked ? new Set(tickets.map((t) => t.id)) : new Set());
  };
  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const r = await api.post("/tickets/bulk-delete", { ids });
      toast.success(`${r.data.deleted} ticket(s) supprimé(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression échouée");
    }
  };

  const doBulkSpam = async (spam) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const r = await api.post("/tickets/bulk-spam", { ids, spam });
      toast.success(spam
        ? `${r.data.updated} ticket(s) marqué(s) comme indésirable`
        : `${r.data.updated} ticket(s) restauré(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible");
    }
  };

  const claimTicket = async (t) => {
    if (!user?.id) return;
    try {
      await api.patch(`/tickets/${t.id}`, { assignee_id: user.id });
      toast.success(`Ticket #${t.number} pris en charge`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de prendre le ticket");
    }
  };

  useEffect(() => { load(); }, [clientIdFilter, page, view, hideClosed, hideAi]);

  const canCreate = user?.role !== "client" || true;

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Tickets</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">
            {clientIdFilter ? `Tickets de ${clientName || "ce client"}` : "Tous les tickets"}
          </h1>
          {clientIdFilter && (
            <Link to="/tickets" className="text-sm text-primary hover:underline" data-testid="clear-client-filter">
              ← Voir tous les tickets
            </Link>
          )}
          {isStaff && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                data-testid="tab-all"
                onClick={() => { setView("all"); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${view === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
              >Tous</button>
              <button
                data-testid="tab-mine"
                onClick={() => { setView("mine"); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${view === "mine" ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
              >Mes tickets assignés</button>
              <button
                data-testid="tab-untaken"
                onClick={() => { setView("untaken"); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 ${view === "untaken" ? "bg-blue-600 text-white border-blue-600" : "bg-background hover:bg-muted text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900"}`}
              ><span className={`w-2 h-2 rounded-full ${view === "untaken" ? "bg-white" : "bg-blue-500"}`}/> Non attribués</button>
              <button
                data-testid="tab-unread"
                onClick={() => { setView("unread"); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 ${view === "unread" ? "bg-blue-600 text-white border-blue-600" : "bg-background hover:bg-muted text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900"}`}
              ><span className={`w-2 h-2 rounded-full ${view === "unread" ? "bg-white" : "bg-blue-500"}`}/> Non lus</button>
              <button
                data-testid="tab-spam"
                onClick={() => { setView("spam"); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1 ${view === "spam" ? "bg-amber-600 text-white border-amber-600" : "bg-background hover:bg-muted text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900"}`}
              ><ShieldWarning size={12}/> Courriers indésirables</button>
              <button
                data-testid="tab-ai"
                onClick={() => { setView("ai"); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1 ${view === "ai" ? "bg-emerald-600 text-white border-emerald-600" : "bg-background hover:bg-muted text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900"}`}
              ><span className={`w-2 h-2 rounded-full ${view === "ai" ? "bg-white" : "bg-emerald-500"}`}/> Flux IA</button>
            </div>
          )}
        </div>
        {(isStaff || canCreate) && (
          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm select-none cursor-pointer" title="Masquer les tickets fermés">
              <Switch
                checked={hideClosed}
                onCheckedChange={(v) => { setHideClosed(!!v); setPage(1); }}
                data-testid="toggle-hide-closed"
              />
              <span className="text-muted-foreground">Masquer les fermés</span>
            </label>
            {isStaff && (
              <label className="inline-flex items-center gap-2 text-sm select-none cursor-pointer" title="Masquer les tickets issus du Flux IA (appels IA)">
                <Switch
                  checked={hideAi}
                  onCheckedChange={(v) => { setHideAi(!!v); setPage(1); }}
                  data-testid="toggle-hide-ai"
                />
                <span className="text-muted-foreground">Masquer les appels IA</span>
              </label>
            )}
            {canCreate && (
              <Button data-testid="create-ticket-btn" onClick={() => nav("/tickets/nouveau")}>
                <Plus size={16} className="mr-2"/> Nouveau ticket
              </Button>
            )}
          </div>
        )}
      </header>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-2" data-testid="tickets-filter-row">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <Input
                data-testid="ticket-search"
                placeholder="Recherche par n°, sujet, description, client…"
                value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="pl-9"
              />
            </div>
            <FilterSelect label="Statut" testid="filter-status" value={status} setValue={setStatus} items={STATUSES} labels={STATUS_LABEL}/>
            <FilterSelect label="Priorité" testid="filter-priority" value={priority} setValue={setPriority} items={PRIORITIES} labels={PRIORITY_LABEL}/>
            <FilterSelect label="Catégorie" testid="filter-category" value={category} setValue={setCategory} items={CATEGORIES} labels={CATEGORY_LABEL}/>
            <div className="w-[135px]">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Du</div>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 text-xs" data-testid="filter-date-from"/>
            </div>
            <div className="w-[135px]">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Au</div>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="h-9 text-xs" data-testid="filter-date-to"/>
            </div>
            {(dateFrom || dateTo) && (
              <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0"
                title="Effacer les dates" data-testid="filter-date-clear"
                onClick={() => { setDateFrom(""); setDateTo(""); }}>
                <X size={14}/>
              </Button>
            )}
            <div className="w-[160px]">
              <Select value={sortMode} onValueChange={setSortMode}>
                <SelectTrigger data-testid="filter-sort" className="h-9"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Plus récents</SelectItem>
                  <SelectItem value="oldest">Plus anciens</SelectItem>
                  <SelectItem value="updated_desc">Récemment mis à jour</SelectItem>
                  <SelectItem value="updated_asc">Anciennement mis à jour</SelectItem>
                  <SelectItem value="number_desc">N° décroissant</SelectItem>
                  <SelectItem value="number_asc">N° croissant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="apply-filters" onClick={load} className="h-9 px-3 shrink-0 bg-black hover:bg-neutral-800 text-white font-semibold tracking-wide" title="Appliquer les filtres">
              GO
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isStaff && (
            <div
              className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b transition-opacity duration-150 ${
                selected.size > 0
                  ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 opacity-100"
                  : "opacity-0 pointer-events-none border-transparent"
              }`}
              aria-hidden={selected.size === 0}
              data-testid="bulk-bar"
            >
              <div className="text-sm text-red-900 dark:text-red-200 font-medium">
                {selected.size || 0} ticket(s) sélectionné(s)
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Annuler</Button>
                {view === "spam" ? (
                  <Button variant="secondary" size="sm" onClick={() => doBulkSpam(false)} data-testid="bulk-notspam-btn">
                    <ShieldCheck size={14} className="mr-1.5"/> Marquer comme légitime
                  </Button>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => doBulkSpam(true)} data-testid="bulk-spam-btn">
                    <ShieldWarning size={14} className="mr-1.5"/> Marquer indésirable
                  </Button>
                )}
                {isAdmin && (
                  <Button variant="destructive" size="sm" onClick={() => setConfirmDel(true)} data-testid="bulk-delete-btn">
                    <Trash size={14} className="mr-1.5"/> Supprimer
                  </Button>
                )}
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]" data-testid="tickets-table">
            <thead className="text-xs uppercase text-muted-foreground border-b">
              <tr>
                {isStaff && (
                  <th className="w-10 py-3 pl-4">
                    <Checkbox
                      data-testid="select-all"
                      checked={allChecked ? true : (someChecked ? "indeterminate" : false)}
                      onCheckedChange={(v) => toggleAll(!!v)}
                    />
                  </th>
                )}
                <th className="text-left py-3 px-4">N°</th>
                <th className="text-left py-3 px-4">Sujet</th>
                <th className="text-left py-3 px-4 hidden md:table-cell">Client</th>
                <th className="text-left py-3 px-4">Catégorie</th>
                <th className="text-left py-3 px-4">Priorité</th>
                <th className="text-left py-3 px-4">Statut</th>
                <th className="text-left py-3 px-4 hidden lg:table-cell">Assigné</th>
                <th className="text-right py-3 pl-4 pr-6 whitespace-nowrap">SLA</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isAdmin ? 9 : 8} className="text-center py-10 text-muted-foreground">Chargement…</td></tr>
              )}
              {!loading && tickets.length === 0 && (
                <tr><td colSpan={isAdmin ? 9 : 8} className="text-center py-10 text-muted-foreground">Aucun ticket trouvé.</td></tr>
              )}
              {tickets.map((t) => {
                const sla = slaCountdown(t.sla_resolution_deadline);
                const urgentPrio = t.priority === "urgente" || t.priority === "critique";
                const rowCls = sla.breached
                  ? "bg-red-50/60 dark:bg-red-950/20 border-l-4 border-l-red-500"
                  : sla.urgent
                    ? "bg-orange-50/50 dark:bg-orange-950/20 border-l-4 border-l-orange-500"
                    : urgentPrio ? "border-l-4 border-l-orange-300" : "";
                const isSel = selected.has(t.id);
                return (
                  <tr key={t.id}
                    data-testid={`ticket-row-${t.number}`}
                    onClick={(e) => openTicketFromEvent(e, `/tickets/${t.id}`, nav)}
                    onAuxClick={(e) => openTicketFromEvent(e, `/tickets/${t.id}`, nav)}
                    className={`border-b hover:bg-muted/40 cursor-pointer ${rowCls} ${isSel ? "bg-red-50/40 dark:bg-red-950/10" : ""} ${t.status === "ferme" ? "text-muted-foreground/70" : ""}`}>
                    {isStaff && (
                      <td className="w-10 py-3 pl-4" data-row-checkbox onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          data-testid={`select-${t.number}`}
                          checked={isSel}
                          onCheckedChange={(v) => toggleOne(t.id, !!v)}
                        />
                      </td>
                    )}
                    <td className="py-3 px-4 mono text-muted-foreground">
                      <Link
                        to={`/tickets/${t.id}`}
                        data-testid={`ticket-number-link-${t.number}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 hover:underline"
                      >
                        {t.is_read === false && (
                          <span
                            data-testid={`unread-dot-${t.number}`}
                            title="Nouvelle réponse client — personne de l'équipe ne l'a encore ouverte"
                            className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0 animate-pulse ring-2 ring-blue-500/30"
                          />
                        )}
                        {t.is_read === true && (t.read_by_names?.length > 0) && (
                          <span
                            data-testid={`read-by-${t.number}`}
                            title={`Ouvert par : ${t.read_by_names.join(", ")}`}
                            className="inline-block w-2 h-2 rounded-full bg-emerald-500/60 shrink-0"
                          />
                        )}
                        #{t.number}
                      </Link>
                    </td>
                    <td className="py-3 px-4">
                      <div className={`truncate max-w-[280px] ${t.is_read === false ? "font-bold text-foreground" : "font-medium"}`}>{t.subject}</div>
                      <div className="text-xs text-muted-foreground">{relTime(t.created_at)}</div>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <div className="font-medium">{t.client_name}</div>
                      <div className="text-xs text-muted-foreground">{t.client_email}</div>
                    </td>
                    <td className="py-3 px-4">
                      {t.is_ai ? (
                        <span
                          data-testid={`ai-badge-${t.number}`}
                          title="Ticket provenant d'un flux IA"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.65rem] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/> IA
                        </span>
                      ) : (
                        <CategoryBadge value={t.category}/>
                      )}
                    </td>
                    <td className="py-3 px-4"><PriorityBadge value={t.priority}/></td>
                    <td className="py-3 px-4"><StatusBadge value={t.status}/></td>
                    <td className="py-3 px-4 hidden lg:table-cell text-sm">{t.assignee_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className={`py-3 pl-4 pr-6 text-right mono text-xs whitespace-nowrap ${sla.breached ? "text-destructive font-semibold" : sla.urgent ? "text-orange-500" : "text-muted-foreground"}`}>
                      <Clock size={12} className="inline mr-1"/>{sla.text}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm">
            <div className="text-muted-foreground">{total} ticket(s)</div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page">Précédent</Button>
              <span className="mono">Page {page} / {pages}</span>
              <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="next-page">Suivant</Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={confirmDel} onOpenChange={setConfirmDel}
        title={`Supprimer ${selected.size} ticket(s) ?`}
        description="Cette action est définitive : les tickets sélectionnés, leurs pièces jointes et l'historique de tracking associés seront supprimés."
        confirmLabel="Supprimer définitivement"
        danger
        onConfirm={doBulkDelete}
        testIdPrefix="bulk-del"
      />
    </div>
  );
}

function FilterSelect({ label, value, setValue, items, labels, testid }) {
  return (
    <div className="w-[140px]">
      <Select value={value} onValueChange={setValue}>
        <SelectTrigger data-testid={testid} className="h-9"><SelectValue placeholder={label}/></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all">Tous · {label}</SelectItem>
          {items.map((k) => <SelectItem key={k} value={k}>{labels[k]}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
